import React, { useMemo } from "react";
import { DollarSign, CheckSquare, Square } from "lucide-react";
import { Button } from "./ui/button";
import { ProcessCard } from "./ProcessCard";
import { formatCurrencyBRL } from "../lib/utils";

export function ProcessList({
  processes,
  selectedIds,
  setSelectedIds,
  processTypes = {},
  onTypeChange,
  showJuri = true,
  showParcelado = true,
  hideCheckbox = false
}) {

  const handleToggle = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(pid => pid !== id));
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === processes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(processes.map(p => p.id));
    }
  };

  const totals = useMemo(() => {
    const selected = processes.filter(p => selectedIds.includes(p.id));

    const juri = selected.reduce((acc, p) => {
      const type = processTypes[p.id] || 'acordo';
      if (type === 'acordo_cessao') {
        return acc + Number(p.valor_proposta_final || 0);
      }
      return acc;
    }, 0);

    const parcelado = selected.reduce((acc, p) => {
      const type = processTypes[p.id] || 'acordo';
      if (type !== 'acordo_cessao') {
        return acc + Number(p.valor_credito || p.valor_proposta_final || 0);
      }
      return acc;
    }, 0);

    const fgts = selected.reduce((acc, p) => {
      const type = processTypes[p.id] || 'acordo';
      if (type !== 'acordo_cessao') {
        return acc + Number(p.valor_fgts || 0);
      }
      return acc;
    }, 0);

    return { juri, parcelado, fgts, total: juri + parcelado };
  }, [processes, selectedIds, processTypes]);

  const hasAnyJuri = useMemo(() => {
    return selectedIds.some(id => processTypes[id] === 'acordo_cessao');
  }, [selectedIds, processTypes]);

  if (!processes || processes.length === 0) {
    return (
      <div className="text-center p-8 bg-slate-50 rounded-xl border border-dashed border-slate-300">
        <p className="text-slate-500">Nenhum processo encontrado.</p>
      </div>
    )
  }

  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-md ${hasAnyJuri ? 'bg-gradient-to-br from-emerald-600 to-emerald-500' : 'bg-gradient-to-br from-blue-600 to-blue-500'}`}>
          <DollarSign className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-800">
          Valores a receber
        </h2>
      </div>

      {!hideCheckbox && (
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            className="flex items-center gap-2"
          >
            {selectedIds.length === processes.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {selectedIds.length === processes.length ? "Desmarcar Todos" : "Selecionar Todos"}
          </Button>
          <span className="text-sm text-slate-500">
            {selectedIds.length} selecionado(s)
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 mb-6">
        {processes.map((process) => (
          <ProcessCard
            key={process.id}
            process={process}
            isSelected={selectedIds.includes(process.id)}
            onToggle={handleToggle}
            selectedType={processTypes[process.id]}
            onTypeChange={onTypeChange}
            showJuri={showJuri}
            showParcelado={showParcelado}
            hideCheckbox={hideCheckbox}
          />
        ))}
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-col gap-2">
          {totals.juri > 0 && (
            <div className="p-4 rounded-xl border-2 flex items-center justify-between bg-emerald-100 border-emerald-300">
              <span className="font-semibold text-emerald-900">Total à Vista (Juri):</span>
              <span className="text-xl font-bold text-emerald-900">{formatCurrencyBRL(totals.juri)}</span>
            </div>
          )}
          {totals.parcelado > 0 && (
            <div className="p-4 rounded-xl border-2 bg-slate-100 border-slate-300 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-700">Total Parcelado:</span>
                <span className="text-xl font-bold text-slate-900">{formatCurrencyBRL(totals.parcelado)}</span>
              </div>
              {totals.fgts > 0 && (
                <div className="flex items-center justify-between border-t border-slate-300 pt-2">
                  <span className="text-sm font-bold text-amber-700">Do qual, via conta vinculada (FGTS):</span>
                  <span className="text-base font-black text-amber-800">{formatCurrencyBRL(totals.fgts)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
